import { useEffect, useRef } from 'react';
import classNames from 'classnames';

import Select from 'components/partials/select/select';
import Tabs from 'components/partials/tabs/tabs';

import CaptureList from 'containers/admin/clients/client/tabs-components/critical-populations/components/capture-list';
import ConfigurationPlaceholder from 'containers/admin/clients/client/tabs-components/critical-populations/components/configuration-placeholder';
import DeleteModal from 'containers/admin/clients/client/tabs-components/critical-populations/components/delete-modal';
import EvaluatingToolbar from 'containers/admin/clients/client/tabs-components/critical-populations/components/evaluating-toolbar';
import FormDrawer from 'containers/admin/clients/client/tabs-components/critical-populations/components/form-drawer';
import {
  PAGE_TITLE,
  TAB_LABELS,
  TAB_ORDER,
  TAB_PANEL_FILL_CLASS,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import {
  getTabCountLabel,
  isCaptureListTab,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type { CriticalPopulationsView } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { useCriticalPopulationsPage } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-critical-populations-page';
import { useRetainedDrawer } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-retained-drawer';
import type { YearOption } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';

interface CriticalPopulationsTabProps {
  clientId: string | number;
}

const YEAR_SELECT_CONTROL_STYLES = {
  padding: '0 8px',
  height: 32,
  minHeight: 32,
  fontSize: 14,
  lineHeight: '20px',
};

const yearSelectStyles = () => ({
  valueContainer: (base: object) => ({ ...base, padding: 0 }),
  dropdownIndicator: (base: object) => ({
    ...base,
    padding: 0,
    svg: { width: 16, height: 16 },
  }),
});

const getPanelId = (tab: CriticalPopulationsView): string =>
  `critical-populations-${tab}-panel`;

const CriticalPopulationsTab = ({ clientId }: CriticalPopulationsTabProps) => {
  const page = useCriticalPopulationsPage(clientId);
  const { drawer } = page;
  const activePanelRef = useRef<HTMLDivElement>(null);
  const { retained, release } = useRetainedDrawer(drawer, page.drawerRow);

  const handleDrawerExited = (): void => {
    if (drawer.mode === 'closed') release();
  };

  const handleDecisionDone = (): void => {
    page.closeDecision();
    activePanelRef.current?.focus();
  };

  const shouldFocusPanelAfterDelete = useRef(false);

  useEffect(() => {
    if (!page.deleteTarget && shouldFocusPanelAfterDelete.current) {
      shouldFocusPanelAfterDelete.current = false;
      activePanelRef.current?.focus();
    }
  }, [page.deleteTarget]);

  const handleDeleted = (): void => {
    shouldFocusPanelAfterDelete.current = true;
    page.closeDelete();
  };

  const numericClientId = Number(clientId);

  const tabItems = TAB_ORDER.map((tab) => ({
    value: tab,
    label: TAB_LABELS[tab],
    panelId: getPanelId(tab),
    count: isCaptureListTab(tab)
      ? getTabCountLabel(page.counts?.[tab])
      : undefined,
  }));

  return (
    <div className="relative">
      <div className="mb-4 mt-5 flex items-center justify-between">
        <h2 className="font-main text-2xl font-bold leading-8 text-navy-800">
          {PAGE_TITLE}
        </h2>
        <Select<YearOption>
          options={page.yearOptions}
          value={page.yearOptions.find(
            (year) => year.value === page.enteringClassYear
          )}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              page.handleYearChange(option.value);
            }
          }}
          selectWrapperClass="!w-20"
          extraBasicStyles={YEAR_SELECT_CONTROL_STYLES}
          extraStyles={yearSelectStyles}
          aria-label="Select year"
          data-testid="critical-populations-year-select"
        />
      </div>

      <Tabs
        items={tabItems}
        activeTab={page.view}
        onSelect={(tab) =>
          page.handleViewChange(tab as CriticalPopulationsView)
        }
        customClass="mb-3 border-navy-100"
        data-testid="critical-populations-tabs"
      />

      {TAB_ORDER.map((tab) => (
        <div
          key={tab}
          ref={page.view === tab ? activePanelRef : undefined}
          id={getPanelId(tab)}
          role="tabpanel"
          tabIndex={-1}
          className={classNames('focus:outline-none', {
            [TAB_PANEL_FILL_CLASS]: page.view === tab,
          })}
          aria-label={TAB_LABELS[tab]}
          hidden={page.view !== tab}
        >
          {page.view === tab && tab === 'configuration' && (
            <ConfigurationPlaceholder />
          )}
          {page.view === tab && isCaptureListTab(tab) && (
            <>
              {tab === 'evaluating' && !page.isLoading && (
                <EvaluatingToolbar
                  count={page.counts?.evaluating}
                  onAddCustom={page.openCreate}
                />
              )}
              <CaptureList
                tab={tab}
                rows={page.grouped[tab]}
                clientId={numericClientId}
                isLoading={page.isLoading}
                isError={page.isError}
                onRetry={page.refetch}
                openPanel={page.openPanel}
                onEdit={page.openEdit}
                onDelete={page.openDelete}
                onOpenDecision={page.openDecision}
                onCloseDecision={page.closeDecision}
                onDecisionDone={handleDecisionDone}
              />
            </>
          )}
        </div>
      ))}

      {page.deleteTarget && isCaptureListTab(page.view) && (
        <DeleteModal
          target={page.deleteTarget}
          tab={page.view}
          clientId={numericClientId}
          onDeleted={handleDeleted}
          onClose={page.closeDelete}
        />
      )}

      {retained?.drawer.mode === 'edit' && retained.row && (
        <FormDrawer
          key={retained.session}
          open={drawer.mode === 'edit'}
          onExited={handleDrawerExited}
          mode="edit"
          row={retained.row}
          clientId={numericClientId}
          enteringClassYear={page.enteringClassYear}
          onClose={page.closeDrawer}
        />
      )}
      {retained?.drawer.mode === 'create' && (
        <FormDrawer
          key={retained.session}
          open={drawer.mode === 'create'}
          onExited={handleDrawerExited}
          mode="create"
          clientId={numericClientId}
          enteringClassYear={page.enteringClassYear}
          onClose={page.closeDrawer}
        />
      )}
    </div>
  );
};

export default CriticalPopulationsTab;
